// src/features/team/StatEditor.tsx

import { X, RotateCcw } from 'lucide-react';
import type { StatBlock, StatKey } from '../../core/types';
import { EV_MAX, EV_TOTAL_MAX, RACE_TOTAL_MAX } from '../../core/stats';
const STAT_KEYS: StatKey[] = ['hp', 'atk', 'spa', 'def', 'spd', 'spe'];

const STAT_LABELS: Record<StatKey, string> = {
  hp: '生命',
  atk: '物攻',
  spa: '魔攻',
  def: '物防',
  spd: '魔防',
  spe: '速度',
};

const BASE_MAX = 200;

interface StatEditorProps {
  open: boolean;
  onClose: () => void;
  spiritName?: string;
  baseStats: StatBlock;            // 左列：种族值
  evs: StatBlock;                  // 右列：个体值（原努力值）
  finalStats?: StatBlock; // 👈 新增这一行
  onChangeBaseStat: (key: StatKey, value: number) => void;
  onChangeEv: (key: StatKey, value: number) => void;
  onResetBaseStats?: () => void;
  onResetEvs?: () => void;
}

export function StatEditor({
  open,
  onClose,
  spiritName,
  baseStats,
  evs,
  finalStats, // 👈 接收
  onChangeBaseStat,
  onChangeEv,
  onResetBaseStats,
  onResetEvs,
}: StatEditorProps) {
  if (!open) return null;

    // 计算左侧六维种族值总和
  const baseTotal = STAT_KEYS.reduce((sum, k) => sum + baseStats[k], 0);
  // 计算右侧个体值总和
  const evTotal = STAT_KEYS.reduce((sum, k) => sum + evs[k], 0);
  
  // 进度条基于种族值总和（按照 800 为参考满值）
  const baseProgress = Math.min(100, (baseTotal / RACE_TOTAL_MAX) * 100);
  const evOverflow = evTotal > EV_TOTAL_MAX;
  const evProgress = Math.min(100, (evTotal / EV_TOTAL_MAX) * 100);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-white">种族值 / 个体值</h2>
            {spiritName && (
              <p className="text-xs text-slate-400 mt-0.5">{spiritName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition"
            aria-label="关闭"
          >
            <X size={20} />
          </button>
        </div>

        {/* 个体值总和进度条 */}
        <div className="px-6 py-3 border-b border-slate-700">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-slate-400">种族值总和</span>
            <span className="text-xs font-mono text-slate-300">
              {baseTotal}
            </span>
          </div>
          <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-all"
              style={{ width: `${baseProgress}%` }}
            />
          </div>
        </div>

        {/* 表头 */}
         <div className="grid grid-cols-[72px_1fr_1fr_1fr] gap-3 px-6 py-2 border-b border-slate-700 text-[10px] uppercase text-slate-500 tracking-wider">
          <span>属性</span>
          <span>种族值 (0-{BASE_MAX})</span>
          <span>个体值 (0-{EV_MAX})</span>
          <span className="text-right">最终数值</span> {/* 👈 新增 */}
        </div>

        {/* 属性行 */}
        <div className="flex-1 overflow-y-auto">
          {STAT_KEYS.map((key) => (
            <div
              key={key}
              className="grid grid-cols-[72px_1fr_1fr_1fr] gap-3 px-6 py-3 border-b border-slate-700/50 items-center"
            >
              <span className="text-sm text-slate-300 font-medium">
                {STAT_LABELS[key]}
              </span>
              <NumberStepper
                value={baseStats[key]}
                min={0}
                max={BASE_MAX}
                onChange={(v) => onChangeBaseStat(key, v)}
              />
              <NumberStepper
                value={evs[key]}
                min={0}
                max={EV_MAX}
                step={60}
                onChange={(v) => onChangeEv(key, v)}
              />
              {/* 👇 新增：最终数值展示 */}
              <span className="text-right text-sm font-mono font-bold text-primary-400">
                {finalStats ? finalStats[key] : '—'}
              </span>
            </div>
          ))}
        </div>

        {/* 底部操作 */}
        <div className="px-6 py-3 border-t border-slate-700 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-2">
            {onResetBaseStats && (
              <button
                onClick={onResetBaseStats}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs transition"
              >
                <RotateCcw size={12} />
                还原种族值
              </button>
            )}
            {onResetEvs && (
              <button
                onClick={onResetEvs}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs transition"
              >
                <RotateCcw size={12} />
                清零个体值
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium transition"
          >
            完成
          </button>
        </div>
      </div>
    </div>
  );
}

/** 数字步进器 */
function NumberStepper({
  value,
  min,
  max,
  step = 1,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
}) {
  const dec = () => onChange(Math.max(min, value - step));
  const inc = () => onChange(Math.min(max, value + step));

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={dec}
        disabled={value <= min}
        className="w-7 h-7 flex items-center justify-center rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed text-white text-sm transition"
      >
        −
      </button>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          if (isNaN(v)) return;
          onChange(Math.max(min, Math.min(max, v)));
        }}
        className="w-14 h-7 text-center bg-slate-900 border border-slate-700 rounded-md text-white text-sm font-mono focus:outline-none focus:border-primary-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <button
        type="button"
        onClick={inc}
        disabled={value >= max}
        className="w-7 h-7 flex items-center justify-center rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed text-white text-sm transition"
      >
        +
      </button>
    </div>
  );
}